import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { injectUser } from '@quipex/core/auth';
import {
    APP_CONFIG_TOKEN,
    FeatureFlagOverrideService,
    FeatureFlagService,
} from '@quipex/core/config';
import {
    QpxAnimatedIcon,
    QpxButton,
    QpxIcon,
    type NavButton,
    QpxTooltip,
} from '@quipex/shared/ui';
import { FeatureFlagDebugPanel } from '../feature-flag-debug-panel/feature-flag-debug-panel';
import { RealtimeDebugPanel } from '../realtime-debug-panel/realtime-debug-panel';
import { ThemeDebugPanel } from '../theme-debug-panel/theme-debug-panel';

@Component({
    selector: 'qpx-utility-dock',
    imports: [
        FeatureFlagDebugPanel,
        MatButtonModule,
        QpxTooltip,
        NgIcon,
        QpxAnimatedIcon,
        QpxButton,
        QpxIcon,
        RealtimeDebugPanel,
        ThemeDebugPanel,
    ],
    templateUrl: './utility-dock.html',
    styleUrl: './utility-dock.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UtilityDock {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly featureFlagOverrideService = inject(
        FeatureFlagOverrideService
    );
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly me = injectUser();
    //#endregion

    //#region Inputs
    public readonly actions = input<readonly NavButton[]>([]);
    //#endregion

    //#region State
    protected readonly minimized = signal(false);
    protected readonly showRealtimeDiagnostics =
        !this.appConfig.useProductionUi && this.appConfig.env === 'development';
    private readonly canUseDemoFeatureFlagOverrides = computed(
        () => this.appConfig.env === 'demo' && this.me()?.isSysAdmin === true
    );
    protected readonly showFeatureFlagDiagnostics = computed(
        () =>
            this.featureFlagService.canOverrideFeatureFlags &&
            (this.appConfig.env !== 'demo' ||
                this.canUseDemoFeatureFlagOverrides())
    );
    protected readonly isVisible = computed(
        () =>
            this.actions().length > 0 ||
            this.showRealtimeDiagnostics ||
            this.showFeatureFlagDiagnostics()
    );
    protected readonly minimizeLabel = computed(() =>
        this.minimized()
            ? 'Expand application utilities'
            : 'Minimise application utilities'
    );
    //#endregion

    constructor() {
        effect(() => {
            this.featureFlagOverrideService.setDemoOverrideAccess(
                this.canUseDemoFeatureFlagOverrides()
            );
        });
    }

    //#region Public methods
    public focusAction(key: string): void {
        const actionButton = [
            ...this.host.nativeElement.querySelectorAll<HTMLButtonElement>(
                '[data-utility-action-key]'
            ),
        ].find((button) => button.dataset['utilityActionKey'] === key);
        actionButton?.focus();
    }
    //#endregion

    //#region Template methods
    protected toggleMinimized(): void {
        this.minimized.update((minimized) => !minimized);
    }
    //#endregion
}
